/**
 * Normalize phone number to E.164 format
 * Ported from functions/utils.js
 */
export function normalizePhone(
  phone: string | null | undefined,
  lenient = true,
): string {
  if (!phone) {
    return '';
  }

  let normalized = phone.replace(/[^\d]/g, '');
  if (normalized.length <= 10) {
    normalized = '1' + normalized;
  }

  if (normalized.length <= 11) {
    normalized = '+' + normalized;
  }

  if (!lenient && normalized.length < 9) {
    return '';
  }

  return normalized;
}

// GSM-7 basic character set (3GPP TS 23.038)
const GSM_7BIT_CHARS =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\u001BÆæßÉ !"#¤%&\'()*+,-./' +
  '0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿' +
  'abcdefghijklmnopqrstuvwxyzäöñüà';

// GSM-7 extension table characters (require ESC + char = 2 septets)
const GSM_7BIT_EXTENDED_CHARS = '^{}\\[~]|€';

function isGsm7(message: string): boolean {
  for (const ch of message) {
    if (
      GSM_7BIT_CHARS.indexOf(ch) === -1 &&
      GSM_7BIT_EXTENDED_CHARS.indexOf(ch) === -1
    ) {
      return false;
    }
  }
  return true;
}

function countGsm7Septets(message: string): number {
  let count = 0;
  for (const ch of message) {
    if (GSM_7BIT_EXTENDED_CHARS.indexOf(ch) !== -1) {
      count += 2;
    } else {
      count += 1;
    }
  }
  return count;
}

/**
 * Calculate the number of SMS segments for a given message.
 * Handles GSM-7 vs UCS-2, emojis, and multi-segment headers.
 */
export function getSmsSegmentCount(message: string): {
  segments: number;
  encoding: 'GSM-7' | 'UCS-2';
  characters: number;
} {
  if (message.length === 0) {
    return { segments: 0, encoding: 'GSM-7', characters: 0 };
  }

  const gsm7 = isGsm7(message);

  if (gsm7) {
    const septets = countGsm7Septets(message);
    const singleLimit = 160;
    const concatLimit = 153;
    const segments =
      septets <= singleLimit ? 1 : Math.ceil(septets / concatLimit);
    return {
      segments,
      encoding: 'GSM-7',
      characters: [...message].length,
    };
  } else {
    const codePoints = [...message].length;
    const singleLimit = 70;
    const concatLimit = 67;
    const segments =
      codePoints <= singleLimit ? 1 : Math.ceil(codePoints / concatLimit);
    return {
      segments,
      encoding: 'UCS-2',
      characters: codePoints,
    };
  }
}

/**
 * Calculate GSM 7-bit segment count for SMS.
 * Delegates to getSmsSegmentCount for accurate GSM-7 vs UCS-2 handling.
 */
export function calculateGsmSegments(message: string): number {
  return getSmsSegmentCount(message ?? '').segments;
}

/**
 * Estimate SMS cost based on segments
 * Default rate: $0.0062 per segment (US pricing, adjust as needed)
 */
const COST_PER_SEGMENT = 0.0062;

export function estimateSmsCost(
  segments: number,
  ratePerSegment = COST_PER_SEGMENT,
): number {
  return segments * ratePerSegment;
}
